import { GAME_CONFIG } from "./config";
import { ZoneId } from "./zones";

const { TILE_SIZE, MAP_COLS, MAP_ROWS } = GAME_CONFIG;

// Tile types
export const TILE = {
  GRASS: 0,    // walkable base ground (both zones)
  ROAD: 1,     // asphalt street (walkable)
  SIDEWALK: 2, // pedestrian pavement (walkable)
  PLAZA: 3,    // plaza slabs (walkable)
  TREE: 4,     // solid deco
  BUILDING: 5, // building footprint (solid)
  BORDER: 6,   // map edge (solid)
  FENCE: 7,    // construction-site barrier (solid)
  DIRT: 8,     // construction-site soil (walkable)
} as const;

// ─── Per-zone village layout ─────────────────────────────────────────────────
// Each zone owns an independent, full-size (MAP_COLS x MAP_ROWS) map, laid
// out from an explicit design spec (row/col rects) rather than an ad-hoc
// arrangement — see the feature/map-redesign PR description for the source
// coordinates this file implements.

export interface Rect { c0: number; r0: number; c1: number; r1: number }

export type BuildingSprite =
  "house_twin" | "house_small" | "store_blue" | "store_brown" | "market_blue" | "market_brown";

// Complete building prefabs (roof + walls + door) from the Modern Exteriors
// pack — footprint size is fixed per sprite (used for collision + anchoring),
// not freely resizable like a tiled wall would be.
const SPRITE_SIZE: Record<BuildingSprite, { w: number; h: number }> = {
  house_twin:   { w: 8, h: 6 },
  house_small:  { w: 4, h: 6 },
  store_blue:   { w: 5, h: 8 },
  store_brown:  { w: 5, h: 8 },
  market_blue:  { w: 5, h: 9 },
  market_brown: { w: 5, h: 9 },
};

export interface BuildingDef {
  id: string;
  label: string; // Korean signage — the tileset has no Korean text, so this
                  // renders as a Phaser text label above the footprint.
  sprite: BuildingSprite;
  col: number; // footprint top-left (tile) — the prefab, centered within the
  row: number; // originally designated lot (see SpecBuilding below)
  w: number;   // == SPRITE_SIZE[sprite].w
  h: number;   // == SPRITE_SIZE[sprite].h
}

// A building as specified by the design doc: a lot (rect) plus which prefab
// to place in it. The prefab's own size wins over the lot size — it's
// centered in the lot, and any leftover lot area stays open ground.
interface SpecBuilding {
  id: string;
  label: string;
  sprite: BuildingSprite;
  lot: Rect;
}

function centerInLot(spec: SpecBuilding): BuildingDef {
  const size = SPRITE_SIZE[spec.sprite];
  const lotW = spec.lot.c1 - spec.lot.c0 + 1;
  const lotH = spec.lot.r1 - spec.lot.r0 + 1;
  const col = spec.lot.c0 + Math.max(0, Math.floor((lotW - size.w) / 2));
  const row = spec.lot.r0 + Math.max(0, Math.floor((lotH - size.h) / 2));
  return { id: spec.id, label: spec.label, sprite: spec.sprite, col, row, w: size.w, h: size.h };
}

interface ConstructionZone {
  rect: Rect;   // perimeter painted as FENCE, interior as DIRT
  gate: Rect;   // opening carved into the fence (walkable DIRT)
}

interface ZoneLayout {
  roads: Rect[];
  sidewalks: Rect[];
  plazas: Rect[];
  buildings: BuildingDef[];
  constructionZone?: ConstructionZone;
  spawn: { x: number; y: number };
  monsterSpawnRects: Record<string, Rect>;
}

function tileCenter(col: number, row: number) {
  return { x: col * TILE_SIZE + TILE_SIZE / 2, y: row * TILE_SIZE + TILE_SIZE / 2 };
}

const GANGNAM_SPEC_BUILDINGS: SpecBuilding[] = [
  // 학원가
  { id: "g_bootcamp",  label: "강남코딩부트캠프", sprite: "house_twin",   lot: { c0: 3,  r0: 4, c1: 11, r1: 13 } },
  { id: "g_academy",   label: "국비지원학원",     sprite: "store_blue",   lot: { c0: 14, r0: 4, c1: 23, r1: 13 } },
  { id: "g_algo",      label: "알고리즘의숲",     sprite: "store_brown",  lot: { c0: 26, r0: 4, c1: 35, r1: 13 } },
  { id: "g_studycafe", label: "스터디카페 커밋",  sprite: "market_blue",  lot: { c0: 38, r0: 4, c1: 46, r1: 13 } },
  // 카페골목
  { id: "g_cafe_semi",  label: "카페 세미콜론",   sprite: "house_small", lot: { c0: 22, r0: 19, c1: 26, r1: 22 } },
  { id: "g_cafe_brew",  label: "브루잉 스탠다드", sprite: "house_small", lot: { c0: 22, r0: 24, c1: 26, r1: 27 } },
  { id: "g_cafe_study", label: "24시 스터디카페", sprite: "house_small", lot: { c0: 28, r0: 19, c1: 32, r1: 22 } },
  // 지하철역 입구 (장식 구조물)
  { id: "g_subway", label: "역삼역 3번출구", sprite: "market_brown", lot: { c0: 7, r0: 30, c1: 11, r1: 32 } },
];

const HONGDAE_SPEC_BUILDINGS: SpecBuilding[] = [
  { id: "h_office",   label: "위워크말고여기",   sprite: "house_twin",   lot: { c0: 3,  r0: 3,  c1: 14, r1: 10 } },
  { id: "h_pivot",    label: "주식회사 피벗",     sprite: "store_blue",   lot: { c0: 24, r0: 3,  c1: 31, r1: 9 } },
  { id: "h_runway",   label: "스타트업 런웨이",   sprite: "store_brown",  lot: { c0: 33, r0: 3,  c1: 40, r1: 9 } },
  { id: "h_teamcook", label: "팀쿡스튜디오",     sprite: "market_blue",  lot: { c0: 24, r0: 12, c1: 31, r1: 18 } },
  { id: "h_mvplabs",  label: "MVP랩스",           sprite: "market_brown", lot: { c0: 33, r0: 12, c1: 40, r1: 18 } },
  { id: "h_center",   label: "마포창업지원센터", sprite: "house_twin",   lot: { c0: 26, r0: 22, c1: 38, r1: 30 } },
  { id: "h_cafe_deadline", label: "카페 데드라인", sprite: "house_small", lot: { c0: 4,  r0: 27, c1: 8,  r1: 31 } },
  { id: "h_cafe_brunch",   label: "브런치서울",   sprite: "house_small", lot: { c0: 10, r0: 27, c1: 14, r1: 31 } },
];

const GANGNAM_LAYOUT: ZoneLayout = {
  roads: [
    { c0: 0, r0: 14, c1: 49, r1: 17 }, // 테헤란대로
  ],
  sidewalks: [
    { c0: 0, r0: 13, c1: 49, r1: 13 }, // 테헤란대로 위쪽 인도
    { c0: 0, r0: 18, c1: 49, r1: 18 }, // 테헤란대로 아래쪽 인도
    { c0: 20, r0: 18, c1: 21, r1: 32 }, // 카페골목 세로 인도
  ],
  plazas: [
    { c0: 3, r0: 19, c1: 16, r1: 29 }, // 중앙광장
  ],
  buildings: GANGNAM_SPEC_BUILDINGS.map(centerInLot),
  constructionZone: {
    rect: { c0: 35, r0: 19, c1: 47, r1: 34 },
    gate: { c0: 35, r0: 26, c1: 35, r1: 27 },
  },
  spawn: tileCenter(9, 22),
  monsterSpawnRects: {
    slime:           { c0: 37, r0: 21, c1: 45, r1: 32 }, // 공사장 내부
    tech_debt_zombie: { c0: 3,  r0: 2,  c1: 46, r1: 3 },  // 뒷골목
  },
};

const HONGDAE_LAYOUT: ZoneLayout = {
  roads: [
    { c0: 18, r0: 2, c1: 21, r1: 37 }, // 메인거리
  ],
  sidewalks: [
    { c0: 17, r0: 2, c1: 17, r1: 37 }, // 메인거리 서쪽 인도
    { c0: 22, r0: 2, c1: 22, r1: 37 }, // 메인거리 동쪽 인도
  ],
  plazas: [
    { c0: 4, r0: 16, c1: 14, r1: 24 }, // 버스킹 광장
  ],
  buildings: HONGDAE_SPEC_BUILDINGS.map(centerInLot),
  spawn: tileCenter(9, 20),
  monsterSpawnRects: {
    cors_gatekeeper:     { c0: 18, r0: 5,  c1: 21, r1: 35 }, // 메인거리 위
    infinite_loop_golem: { c0: 42, r0: 3,  c1: 47, r1: 35 }, // 동쪽 뒷골목
    tech_debt_zombie:    { c0: 26, r0: 32, c1: 38, r1: 35 }, // 창업지원센터 뒤
  },
};

const LAYOUTS: Record<ZoneId, ZoneLayout> = {
  zone_gangnam: GANGNAM_LAYOUT,
  zone_hongdae: HONGDAE_LAYOUT,
};

// ─── Grid construction ──────────────────────────────────────────────────────

function paint(grid: number[][], r: Rect, type: number) {
  for (let row = r.r0; row <= r.r1; row++) {
    for (let col = r.c0; col <= r.c1; col++) {
      if (row < 0 || row >= MAP_ROWS || col < 0 || col >= MAP_COLS) continue;
      grid[row][col] = type;
    }
  }
}

function paintBorderOf(grid: number[][], r: Rect, type: number) {
  paint(grid, { c0: r.c0, r0: r.r0, c1: r.c1, r1: r.r0 }, type); // top
  paint(grid, { c0: r.c0, r0: r.r1, c1: r.c1, r1: r.r1 }, type); // bottom
  paint(grid, { c0: r.c0, r0: r.r0, c1: r.c0, r1: r.r1 }, type); // left
  paint(grid, { c0: r.c1, r0: r.r0, c1: r.c1, r1: r.r1 }, type); // right
}

// Deterministic pseudo-random tree scatter along the map's outer edge —
// same seed every build so the layout is stable across server restarts.
const EDGE_BAND = 2; // rows/cols 0-1 and (MAX-2)..(MAX-1) are the "edge"
function scatterEdgeTrees(grid: number[][]) {
  for (let row = 0; row < MAP_ROWS; row++) {
    for (let col = 0; col < MAP_COLS; col++) {
      const onEdge =
        row < EDGE_BAND || row >= MAP_ROWS - EDGE_BAND ||
        col < EDGE_BAND || col >= MAP_COLS - EDGE_BAND;
      if (!onEdge) continue;
      if (grid[row][col] !== TILE.GRASS) continue;
      if ((row * 31 + col * 17) % 5 === 0) grid[row][col] = TILE.TREE;
    }
  }
}

function buildGrid(layout: ZoneLayout): number[][] {
  const grid: number[][] = [];
  for (let row = 0; row < MAP_ROWS; row++) {
    grid.push(new Array<number>(MAP_COLS).fill(TILE.GRASS));
  }

  for (const r of layout.roads) paint(grid, r, TILE.ROAD);
  for (const s of layout.sidewalks) paint(grid, s, TILE.SIDEWALK);
  for (const p of layout.plazas) paint(grid, p, TILE.PLAZA);

  if (layout.constructionZone) {
    const { rect, gate } = layout.constructionZone;
    paint(grid, rect, TILE.DIRT);
    paintBorderOf(grid, rect, TILE.FENCE);
    paint(grid, gate, TILE.DIRT);
  }

  // Buildings paint last among ground features — they front directly onto
  // sidewalks/roads, same as real street-facing shops.
  for (const b of layout.buildings) {
    paint(grid, { c0: b.col, r0: b.row, c1: b.col + b.w - 1, r1: b.row + b.h - 1 }, TILE.BUILDING);
  }

  scatterEdgeTrees(grid);

  // Border ring last — nothing may override it
  paint(grid, { c0: 0, r0: 0, c1: MAP_COLS - 1, r1: 0 }, TILE.BORDER);
  paint(grid, { c0: 0, r0: MAP_ROWS - 1, c1: MAP_COLS - 1, r1: MAP_ROWS - 1 }, TILE.BORDER);
  paint(grid, { c0: 0, r0: 0, c1: 0, r1: MAP_ROWS - 1 }, TILE.BORDER);
  paint(grid, { c0: MAP_COLS - 1, r0: 0, c1: MAP_COLS - 1, r1: MAP_ROWS - 1 }, TILE.BORDER);

  return grid;
}

const GRIDS: Record<ZoneId, readonly number[][]> = {
  zone_gangnam: buildGrid(GANGNAM_LAYOUT),
  zone_hongdae: buildGrid(HONGDAE_LAYOUT),
};

export function getBuildings(zone: ZoneId): BuildingDef[] {
  return LAYOUTS[zone].buildings;
}

export function getZoneSpawn(zone: ZoneId): { x: number; y: number } {
  return LAYOUTS[zone].spawn;
}

// Random tile-center point within a monster template's designated spawn rect
// for this zone, or undefined if the template doesn't spawn here at all.
export function getMonsterSpawnRect(zone: ZoneId, templateId: string): Rect | undefined {
  return LAYOUTS[zone].monsterSpawnRects[templateId];
}

// Default spawn for brand-new characters (gangnam, near the tutorial NPC).
export const DEFAULT_SPAWN = GANGNAM_LAYOUT.spawn;

export function tileAt(zone: ZoneId, row: number, col: number): number {
  if (row < 0 || row >= MAP_ROWS || col < 0 || col >= MAP_COLS) return TILE.BORDER;
  return GRIDS[zone][row][col];
}

// Trees, buildings, fences and the border block movement
export function isSolidTile(type: number): boolean {
  return type === TILE.TREE || type === TILE.BUILDING || type === TILE.BORDER || type === TILE.FENCE;
}

// Is the given world-pixel position inside a solid tile (or out of bounds)?
export function isSolidAt(zone: ZoneId, x: number, y: number): boolean {
  const col = Math.floor(x / TILE_SIZE);
  const row = Math.floor(y / TILE_SIZE);
  if (row < 0 || row >= MAP_ROWS || col < 0 || col >= MAP_COLS) return true;
  return isSolidTile(tileAt(zone, row, col));
}
